import React, {FC, useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {useAuth} from '../../auth'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import {useProcurementPermissions} from '../core/permissionAccess'
import {PROCUREMENT_PERMS} from '../core/permissions'
import {
  CurrencyExchangeRate,
  CurrencyExchangeRateScopeType,
  CurrencyExchangeRateUpsertRequest,
  CurrencyRateSourceType,
  CompanyGroupAdmin,
  Vendor,
} from '../core/_models'
import {
  deactivateCurrencyExchangeRate,
  getCompanyGroupAdmins,
  getCurrencyExchangeRates,
  getVendors,
  updateCurrencyExchangeRate,
  upsertLatestCurrencyExchangeRate,
} from '../core/_requests'
import {getCurrencies, type CurrencyOption} from '../../Management/core/_requests'
import {resolveProcurementRoleName} from '../core/roles'

type FilterState = {
  sourceCurrencyCode: string
  targetCurrencyCode: string
  sourceType: string
  scopeType: CurrencyExchangeRateScopeType | ''
  activeStatus: '' | 'ACTIVE' | 'INACTIVE'
}

type RateFormState = {
  sourceCurrencyCode: string
  targetCurrencyCode: string
  exchangeRate: string
  effectiveFrom: string
  scopeType: CurrencyExchangeRateScopeType
  companyGroupAdminId: string
  vendorId: string
}

const defaultFilters: FilterState = {
  sourceCurrencyCode: '',
  targetCurrencyCode: '',
  sourceType: '',
  scopeType: '',
  activeStatus: 'ACTIVE',
}

const defaultForm = (): RateFormState => ({
  sourceCurrencyCode: '',
  targetCurrencyCode: '',
  exchangeRate: '',
  effectiveFrom: new Date().toISOString().slice(0, 10),
  scopeType: 'GLOBAL',
  companyGroupAdminId: '',
  vendorId: '',
})

const editableSourceTypes: CurrencyRateSourceType[] = [
  'SYSTEM_REFERENCE',
  'COMPANY_DEFINED',
  'VENDOR_PROVIDED',
  'MANUAL_TRANSACTION',
]

const normalizeRateValue = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return String(value)
}

const resolveScopeType = (row: Pick<CurrencyExchangeRate, 'companyGroupAdminId' | 'vendorId'>): CurrencyExchangeRateScopeType => {
  if (row.vendorId != null) return 'VENDOR'
  if (row.companyGroupAdminId != null) return 'COMPANY_GROUP'
  return 'GLOBAL'
}

const formatScope = (
  row: Pick<CurrencyExchangeRate, 'companyGroupAdminId' | 'companyName' | 'vendorId' | 'vendorName'>,
  companyMap: Map<number, CompanyGroupAdmin>,
  vendorMap: Map<number, Vendor>
): string => {
  const scopeType = resolveScopeType(row)
  if (scopeType === 'COMPANY_GROUP') {
    const companyName =
      row.companyName ||
      (row.companyGroupAdminId != null ? companyMap.get(row.companyGroupAdminId)?.name : null) ||
      `Company Group #${row.companyGroupAdminId}`
    return `Company Group: ${companyName}`
  }
  if (scopeType === 'VENDOR') {
    const vendorName =
      row.vendorName ||
      (row.vendorId != null ? vendorMap.get(row.vendorId)?.name : null) ||
      `Vendor #${row.vendorId}`
    return `Vendor: ${vendorName}`
  }
  return 'Global'
}

const CurrencyExchangeRatesPanel: FC = () => {
  const {currentUser, auth} = useAuth()
  const {can} = useProcurementPermissions()
  const role = resolveProcurementRoleName(currentUser, auth)
  const isSuperAdmin = role === 'SUPER_ADMIN'
  const isCompanyGroupAdmin = role === 'COMPANY_GROUP_ADMIN'
  const canRead = can(PROCUREMENT_PERMS.READ_CURRENCY_EXCHANGE_RATE)
  const canCreate = can(PROCUREMENT_PERMS.CREATE_CURRENCY_EXCHANGE_RATE)
  const canUpdate = can(PROCUREMENT_PERMS.UPDATE_CURRENCY_EXCHANGE_RATE)
  const canDeactivate = can(PROCUREMENT_PERMS.DEACTIVATE_CURRENCY_EXCHANGE_RATE)
  const canCreateManualRate = canCreate && (isSuperAdmin || isCompanyGroupAdmin)

  const [currencies, setCurrencies] = useState<CurrencyOption[]>([])
  const [companyGroups, setCompanyGroups] = useState<CompanyGroupAdmin[]>([])
  const [vendors, setVendors] = useState<Vendor[]>([])
  const [rates, setRates] = useState<CurrencyExchangeRate[]>([])
  const [filters, setFilters] = useState<FilterState>(defaultFilters)
  const [loading, setLoading] = useState(false)
  const [lookupLoading, setLookupLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingRateId, setEditingRateId] = useState<number | null>(null)
  const [form, setForm] = useState<RateFormState>(defaultForm)
  const [saving, setSaving] = useState(false)
  const authenticatedCompanyGroupId = useMemo(() => {
    const raw =
      (currentUser as any)?.cgaid ??
      (currentUser as any)?.companyGroupAdminId ??
      (currentUser as any)?.companyGroupAdmin?.id ??
      (currentUser as any)?.vessel?.companyGroupAdminId
    const value = Number(raw)
    return Number.isFinite(value) && value > 0 ? value : null
  }, [currentUser])

  const companyMap = useMemo(
    () => new Map(companyGroups.map((company) => [company.id, company])),
    [companyGroups]
  )
  const vendorMap = useMemo(
    () => new Map(vendors.map((vendor) => [vendor.id, vendor])),
    [vendors]
  )

  const visibleRates = useMemo(() => {
    return rates.filter((row) => {
      if (filters.sourceCurrencyCode && row.sourceCurrencyCode !== filters.sourceCurrencyCode) return false
      if (filters.targetCurrencyCode && row.targetCurrencyCode !== filters.targetCurrencyCode) return false
      if (filters.sourceType && row.sourceType !== filters.sourceType) return false
      if (filters.activeStatus === 'ACTIVE' && !row.active) return false
      if (filters.activeStatus === 'INACTIVE' && row.active) return false
      if (filters.scopeType && resolveScopeType(row) !== filters.scopeType) return false
      return true
    })
  }, [filters, rates])

  const resetModal = () => {
    setEditingRateId(null)
    setForm(defaultForm())
    setShowModal(false)
  }

  const loadLookups = async () => {
    if (!canRead) return
    setLookupLoading(true)
    try {
      const [currencyRows, companyRows, vendorPage] = await Promise.all([
        getCurrencies(),
        getCompanyGroupAdmins(),
        getVendors(0, 1000),
      ])
      const activeCurrencies = currencyRows.filter((row) => row.active !== false)
      setCurrencies(activeCurrencies)
      setCompanyGroups(companyRows)
      setVendors(Array.isArray(vendorPage?.content) ? vendorPage.content : [])
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load currency rate lookups')
    } finally {
      setLookupLoading(false)
    }
  }

  const loadRates = async () => {
    if (!canRead) return
    setLoading(true)
    setError(null)
    try {
      const data = await getCurrencyExchangeRates({
        sourceCurrencyCode: filters.sourceCurrencyCode || undefined,
        targetCurrencyCode: filters.targetCurrencyCode || undefined,
        sourceType: (filters.sourceType || undefined) as CurrencyRateSourceType | undefined,
        active: filters.activeStatus === '' ? undefined : filters.activeStatus === 'ACTIVE',
      })
      setRates(Array.isArray(data) ? data : [])
    } catch (err: any) {
      const message = err?.message || 'Failed to load currency exchange rates'
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadLookups()
  }, [canRead])

  useEffect(() => {
    void loadRates()
  }, [canRead, filters.sourceCurrencyCode, filters.targetCurrencyCode, filters.sourceType, filters.activeStatus])

  const openCreateModal = () => {
    setEditingRateId(null)
    setForm((prev) => ({
      ...defaultForm(),
      sourceCurrencyCode: prev.sourceCurrencyCode || currencies[0]?.code || '',
      targetCurrencyCode: prev.targetCurrencyCode || currencies[1]?.code || currencies[0]?.code || '',
      scopeType: isCompanyGroupAdmin ? 'COMPANY_GROUP' : 'GLOBAL',
      companyGroupAdminId:
        isCompanyGroupAdmin && authenticatedCompanyGroupId != null
          ? String(authenticatedCompanyGroupId)
          : '',
    }))
    setShowModal(true)
  }

  const openEditModal = (row: CurrencyExchangeRate) => {
    setEditingRateId(row.id ?? null)
    setForm({
      sourceCurrencyCode: row.sourceCurrencyCode,
      targetCurrencyCode: row.targetCurrencyCode,
      exchangeRate: normalizeRateValue(row.exchangeRate),
      effectiveFrom: row.effectiveFrom,
      scopeType: resolveScopeType(row),
      companyGroupAdminId: row.companyGroupAdminId != null ? String(row.companyGroupAdminId) : '',
      vendorId: row.vendorId != null ? String(row.vendorId) : '',
    })
    setShowModal(true)
  }

  const validateForm = (): CurrencyExchangeRateUpsertRequest | null => {
    const sourceCurrencyCode = form.sourceCurrencyCode.trim().toUpperCase()
    const targetCurrencyCode = form.targetCurrencyCode.trim().toUpperCase()
    if (!sourceCurrencyCode || !targetCurrencyCode) {
      toast.error('Source currency and target currency are required.')
      return null
    }
    if (sourceCurrencyCode === targetCurrencyCode) {
      toast.error('Source currency and target currency cannot be the same.')
      return null
    }
    const numericRate = Number(form.exchangeRate)
    if (!Number.isFinite(numericRate) || numericRate <= 0) {
      toast.error('Rate must be greater than zero.')
      return null
    }
    if (!form.effectiveFrom) {
      toast.error('Effective date is required.')
      return null
    }

    let companyGroupAdminId: number | null = null
    let vendorId: number | null = null
    const scopeType = isCompanyGroupAdmin ? 'COMPANY_GROUP' : form.scopeType
    if (scopeType === 'COMPANY_GROUP') {
      if (isCompanyGroupAdmin && authenticatedCompanyGroupId == null) {
        toast.error('Your authenticated company group could not be resolved.')
        return null
      }
      if (!isCompanyGroupAdmin && !form.companyGroupAdminId) {
        toast.error('Company Group is required for company-group scope.')
        return null
      }
      companyGroupAdminId = isCompanyGroupAdmin
        ? authenticatedCompanyGroupId
        : Number(form.companyGroupAdminId)
    }
    if (isSuperAdmin && form.scopeType === 'VENDOR') {
      if (!form.vendorId) {
        toast.error('Vendor is required for vendor scope.')
        return null
      }
      vendorId = Number(form.vendorId)
    }
    if (companyGroupAdminId != null && vendorId != null) {
      toast.error('Company Group and Vendor scope cannot both be set.')
      return null
    }

    return {
      sourceCurrencyCode,
      targetCurrencyCode,
      exchangeRate: form.exchangeRate.trim(),
      effectiveFrom: form.effectiveFrom,
      effectiveTo: null,
      companyGroupAdminId,
      vendorId,
    }
  }

  const handleSave = async () => {
    const payload = validateForm()
    if (!payload) return
    if (editingRateId == null && !canCreateManualRate) {
      toast.error('You do not have permission to create exchange rates.')
      return
    }
    if (editingRateId != null && !canUpdate) {
      toast.error('You do not have permission to update exchange rates.')
      return
    }

    setSaving(true)
    try {
      if (editingRateId != null) {
        await updateCurrencyExchangeRate(editingRateId, payload)
      } else {
        await upsertLatestCurrencyExchangeRate(payload)
      }
      toast.success(editingRateId != null ? 'Exchange rate updated.' : 'Exchange rate saved.')
      resetModal()
      await loadRates()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save currency exchange rate')
    } finally {
      setSaving(false)
    }
  }

  const handleDeactivate = async (row: CurrencyExchangeRate) => {
    if (!row.id) return
    if (!window.confirm(`Deactivate ${row.sourceCurrencyCode} -> ${row.targetCurrencyCode}?`)) {
      return
    }
    try {
      await deactivateCurrencyExchangeRate(row.id)
      toast.success('Exchange rate deactivated.')
      await loadRates()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to deactivate currency exchange rate')
    }
  }

  if (!canRead) {
    return <div className='alert alert-warning m-6'>You do not have access to this page.</div>
  }

  return (
    <div className='container-fluid py-4'>
      <div className='card shadow-sm'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 mb-5'>
            <div>
              <h3 className='mb-1'>Currency Exchange Rates</h3>
              <div className='text-muted small'>
                Manage current manual procurement exchange rates without calling external sync.
              </div>
            </div>
            <div className='d-flex gap-2'>
              <button className='btn btn-light btn-sm' onClick={() => void loadRates()} disabled={loading}>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
              {canCreateManualRate ? (
                <button className='btn btn-primary btn-sm' onClick={openCreateModal}>
                  Add Rate
                </button>
              ) : null}
            </div>
          </div>

          <div className='row g-3 mb-5'>
            <div className='col-md-2'>
              <label className='form-label'>Source currency</label>
              <SearchableSelect
                id='filter-source-currency'
                value={filters.sourceCurrencyCode}
                onChange={(e) => setFilters((prev) => ({...prev, sourceCurrencyCode: e.target.value}))}
              >
                <option value=''>All</option>
                {currencies.map((currency) => (
                  <option key={`filter-source-${currency.code}`} value={currency.code}>
                    {currency.code} - {currency.displayName}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Target currency</label>
              <SearchableSelect
                id='filter-target-currency'
                value={filters.targetCurrencyCode}
                onChange={(e) => setFilters((prev) => ({...prev, targetCurrencyCode: e.target.value}))}
              >
                <option value=''>All</option>
                {currencies.map((currency) => (
                  <option key={`filter-target-${currency.code}`} value={currency.code}>
                    {currency.code} - {currency.displayName}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Source type</label>
              <SearchableSelect
                id='filter-source-type'
                value={filters.sourceType}
                onChange={(e) => setFilters((prev) => ({...prev, sourceType: e.target.value}))}
              >
                <option value=''>All</option>
                {editableSourceTypes.map((sourceType) => (
                  <option key={sourceType} value={sourceType}>
                    {sourceType}
                  </option>
                ))}
              </SearchableSelect>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Scope</label>
              <SearchableSelect
                id='filter-scope-type'
                value={filters.scopeType}
                onChange={(e) => setFilters((prev) => ({...prev, scopeType: e.target.value as FilterState['scopeType']}))}
              >
                <option value=''>All</option>
                <option value='GLOBAL'>Global</option>
                <option value='COMPANY_GROUP'>Company Group</option>
                <option value='VENDOR'>Vendor</option>
              </SearchableSelect>
            </div>
            <div className='col-md-2'>
              <label className='form-label'>Active status</label>
              <SearchableSelect
                id='filter-active-status'
                value={filters.activeStatus}
                onChange={(e) => setFilters((prev) => ({...prev, activeStatus: e.target.value as FilterState['activeStatus']}))}
              >
                <option value=''>All</option>
                <option value='ACTIVE'>Active</option>
                <option value='INACTIVE'>Inactive</option>
              </SearchableSelect>
            </div>
            <div className='col-md-2 d-flex align-items-end'>
              <button
                className='btn btn-light btn-sm w-100'
                onClick={() => setFilters(defaultFilters)}
              >
                Clear Filters
              </button>
            </div>
          </div>

          {lookupLoading ? <div className='text-muted mb-3'>Loading currencies, companies, and vendors...</div> : null}
          {error ? <div className='alert alert-danger mb-3'>{error}</div> : null}

          <div className='table-responsive'>
            <table className='table table-row-dashed align-middle'>
              <thead>
                <tr className='text-muted fw-semibold text-uppercase fs-7'>
                  <th>Source currency</th>
                  <th>Target currency</th>
                  <th>Rate</th>
                  <th>Scope</th>
                  <th>Effective date</th>
                  <th>Active status</th>
                  <th>Updated at</th>
                  <th className='text-end'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className='text-center py-8 text-muted'>
                      Loading exchange rates...
                    </td>
                  </tr>
                ) : visibleRates.length === 0 ? (
                  <tr>
                    <td colSpan={8} className='text-center py-8 text-muted'>
                      No exchange rates found.
                    </td>
                  </tr>
                ) : (
                  visibleRates.map((row) => (
                    <tr key={row.id ?? `${row.sourceCurrencyCode}-${row.targetCurrencyCode}-${row.effectiveFrom}`}>
                      <td>{row.sourceCurrencyCode}</td>
                      <td>{row.targetCurrencyCode}</td>
                      <td>{normalizeRateValue(row.exchangeRate)}</td>
                      <td>{formatScope(row, companyMap, vendorMap)}</td>
                      <td>{row.effectiveFrom}</td>
                      <td>
                        <span className={`badge ${row.active ? 'badge-light-success' : 'badge-light-secondary'}`}>
                          {row.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{row.updatedAt || row.createdAt || '-'}</td>
                      <td className='text-end'>
                        <div className='d-inline-flex gap-2'>
                          {canUpdate ? (
                            <button className='btn btn-light-primary btn-sm' onClick={() => openEditModal(row)}>
                              Edit
                            </button>
                          ) : null}
                          {row.active && canDeactivate ? (
                            <button className='btn btn-light-danger btn-sm' onClick={() => void handleDeactivate(row)}>
                              Deactivate
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal ? (
        <div
          className='modal fade show d-flex align-items-center justify-content-center'
          tabIndex={-1}
          style={{
            backgroundColor: 'rgba(0,0,0,0.45)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-lg modal-dialog-centered'>
            <div className='modal-content bg-white'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingRateId != null ? 'Edit Exchange Rate' : 'Add Exchange Rate'}</h5>
                <button type='button' className='btn-close' onClick={resetModal}></button>
              </div>
              <div className='modal-body'>
                <div className='row g-3'>
                  <div className='col-md-6'>
                    <label className='form-label' htmlFor='form-source-currency'>Source currency</label>
                    <SearchableSelect
                      id='form-source-currency'
                      value={form.sourceCurrencyCode}
                      onChange={(e) => setForm((prev) => ({...prev, sourceCurrencyCode: e.target.value}))}
                    >
                      <option value=''>Select</option>
                      {currencies.map((currency) => (
                        <option key={`form-source-${currency.code}`} value={currency.code}>
                          {currency.code} - {currency.displayName}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label' htmlFor='form-target-currency'>Target currency</label>
                    <SearchableSelect
                      id='form-target-currency'
                      value={form.targetCurrencyCode}
                      onChange={(e) => setForm((prev) => ({...prev, targetCurrencyCode: e.target.value}))}
                    >
                      <option value=''>Select</option>
                      {currencies.map((currency) => (
                        <option key={`form-target-${currency.code}`} value={currency.code}>
                          {currency.code} - {currency.displayName}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label' htmlFor='form-exchange-rate'>Rate</label>
                    <input
                      id='form-exchange-rate'
                      className='form-control'
                      type='text'
                      value={form.exchangeRate}
                      onChange={(e) => setForm((prev) => ({...prev, exchangeRate: e.target.value}))}
                      placeholder='e.g. 83.2500000000'
                    />
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label' htmlFor='form-effective-from'>Effective date</label>
                    <input
                      id='form-effective-from'
                      className='form-control'
                      type='date'
                      value={form.effectiveFrom}
                      onChange={(e) => setForm((prev) => ({...prev, effectiveFrom: e.target.value}))}
                    />
                  </div>
                  {isSuperAdmin ? <div className='col-md-6'>
                    <label className='form-label' htmlFor='form-scope-type'>Scope type</label>
                    <SearchableSelect
                      id='form-scope-type'
                      value={form.scopeType}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          scopeType: e.target.value as CurrencyExchangeRateScopeType,
                          companyGroupAdminId: e.target.value === 'COMPANY_GROUP' ? prev.companyGroupAdminId : '',
                          vendorId: e.target.value === 'VENDOR' ? prev.vendorId : '',
                        }))
                      }
                    >
                      <option value='GLOBAL'>GLOBAL</option>
                      <option value='COMPANY_GROUP'>COMPANY_GROUP</option>
                      <option value='VENDOR'>VENDOR</option>
                    </SearchableSelect>
                  </div> : null}
                  {isSuperAdmin && form.scopeType === 'COMPANY_GROUP' ? (
                    <div className='col-md-12'>
                      <label className='form-label' htmlFor='form-company-group'>Company Group selector</label>
                      <SearchableSelect
                        id='form-company-group'
                        value={form.companyGroupAdminId}
                        onChange={(e) => setForm((prev) => ({...prev, companyGroupAdminId: e.target.value, vendorId: ''}))}
                      >
                        <option value=''>Select company group</option>
                        {companyGroups.map((company) => (
                          <option key={`form-company-${company.id}`} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  ) : null}
                  {isSuperAdmin && form.scopeType === 'VENDOR' ? (
                    <div className='col-md-12'>
                      <label className='form-label' htmlFor='form-vendor'>Vendor selector</label>
                      <SearchableSelect
                        id='form-vendor'
                        value={form.vendorId}
                        onChange={(e) => setForm((prev) => ({...prev, vendorId: e.target.value, companyGroupAdminId: ''}))}
                      >
                        <option value=''>Select vendor</option>
                        {vendors.map((vendor) => (
                          <option key={`form-vendor-${vendor.id}`} value={vendor.id}>
                            {vendor.name}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className='modal-footer'>
                <button type='button' className='btn btn-light btn-sm' onClick={resetModal} disabled={saving}>
                  Cancel
                </button>
                <button type='button' className='btn btn-primary btn-sm' onClick={() => void handleSave()} disabled={saving}>
                  {saving ? 'Saving...' : editingRateId != null ? 'Save Changes' : 'Save Rate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default CurrencyExchangeRatesPanel
